import { first } from 'lodash';
import { Observable } from 'rxjs';
import { IMobizHttpService, IMobizStateService, PagedResults, } from '../../common-react/core';
import { Inject, Injectable, InjectionToken } from '../../common-react/injection';

import { ExternalSearchResult } from '../data/external-search-result.do';
import { GlobalSearchResults } from '../data/global-search-results.do';
import { SearchResult } from '../data/search-result.do';
import { PosturinnApiEndpointConfiguration } from '../other/api-endpoint-configuration.interface';
import { PosturinnServerApi } from '../other/posturinn-server-api.enum';
import { ResourceType } from '../other/search-resource-type.enum';


export interface IGlobalSearchQueryParameters {
  term?: string;
  resourceType?: ResourceType;
  maxResultsPerResourceType?: number;
}

export interface ISearchQueryParameters {
  term?: string;
  page?: number;
  pageSize?: number;
}

export interface ISearchService {
  globalSearch(queryParameters: IGlobalSearchQueryParameters): Observable<GlobalSearchResults>;
  searchMyShipments(queryParameters: ISearchQueryParameters): Observable<PagedResults<SearchResult>>;
  searchExternalShipments(queryParameters: ISearchQueryParameters): Observable<PagedResults<ExternalSearchResult>>;
}

export const ISearchService =
  new InjectionToken<ISearchService>('ISearchService');

@Injectable()
export class SearchService implements ISearchService {

  private readonly accountId: string;

  constructor(
    @Inject(IMobizHttpService)
    private httpService: IMobizHttpService,
    @Inject(IMobizStateService)
    private stateService: IMobizStateService,
  ) {
    if (this.stateService.currentUser.value === null) {
      // NOTE: TP-8573 Display an "Access Denied" error page.
      throw new Error('Access Denied. Please log in.');
    }
    const membership = first(this.stateService.currentUser.value.membership);
    if (!membership) {
      throw new Error('Invalid membership data.');
    }
    // Posturinn accounts have their own membership organization.
    this.accountId = membership.organizationId;
  }

  public searchMyShipments(queryParameters?: ISearchQueryParameters): Observable<PagedResults<SearchResult>> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Search,
      urlParameters: {
        accountId: this.accountId,
        type: ResourceType.MyShipments,
      },
      queryParameters: {
        ...queryParameters,
      },
    };
    const list: PagedResults<SearchResult> = new PagedResults(SearchResult);
    return this.httpService.get(list, config);
  }

  public searchExternalShipments(queryParameters?: ISearchQueryParameters): Observable<PagedResults<ExternalSearchResult>> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Search,
      urlParameters: {
        accountId: this.accountId,
        type: ResourceType.ExternalShipment,
      },
      queryParameters: {
        ...queryParameters,
      },
    };
    const list: PagedResults<ExternalSearchResult> = new PagedResults(ExternalSearchResult);
    return this.httpService.get(list, config);
  }

  public globalSearch(queryParameters?: IGlobalSearchQueryParameters): Observable<GlobalSearchResults> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.GlobalSearch,
      urlParameters: {
        accountId: this.accountId,
      },
      queryParameters: {
        ...queryParameters,
      },
    };
    const searchResults: GlobalSearchResults = new GlobalSearchResults();
    return this.httpService.get(searchResults, config);
  }
}
